/**
 * The phone's connection to an attempt.
 *
 * The phone has no login. It claims a pair code once, receives a device token,
 * and from then on identifies itself with `X-Device-Token`. The token is kept
 * in sessionStorage so that an accidental reload — or Android killing the tab
 * to reclaim memory — reconnects silently instead of asking the student to walk
 * back to the laptop for a new code.
 *
 * The heartbeat is the only channel the phone has. Each beat returns the stage
 * the laptop has published, which is what the phone renders. The phone never
 * decides the stage itself.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AttemptStage,
  AttemptStatus,
  ClaimPairCodeResponse,
  MediaCounts,
} from "@exam/shared";
import { HEARTBEAT_INTERVAL_MS } from "@exam/shared";
import { claimPairCode, heartbeat } from "@/lib/exam-api";
import { isApiError } from "@/lib/api";
import { ServerClock, timed } from "@/lib/exam/serverClock";

type SessionState = "connecting" | "connected" | "reconnecting" | "failed";

function tokenKey(pairCode: string): string {
  return `ep:phone:token:${pairCode}`;
}

/**
 * In-flight claims, shared across every caller for a given code.
 *
 * Claiming twice is NOT harmless: each claim mints a fresh device token and
 * invalidates the previous one, so two overlapping claims race and whichever
 * response is stored last may hold the dead token — every heartbeat after that
 * is 403 and the phone looks permanently disconnected.
 *
 * React StrictMode makes this happen on every mount in development, but the
 * underlying hazard is real in production too (a remount, a fast retry). One
 * shared promise per code removes it at the source.
 */
const inFlightClaims = new Map<string, Promise<ClaimPairCodeResponse>>();

function claimOnce(pairCode: string): Promise<ClaimPairCodeResponse> {
  const existing = inFlightClaims.get(pairCode);
  if (existing) return existing;

  const promise = claimPairCode(pairCode).finally(() => {
    inFlightClaims.delete(pairCode);
  });
  inFlightClaims.set(pairCode, promise);
  return promise;
}

export interface PhoneSession {
  state: SessionState;
  errorMessage: string | null;
  attemptId: string | null;
  deviceToken: string | null;
  examTitle: string | null;
  studentName: string | null;
  /** What the laptop wants this phone to be doing. */
  stage: AttemptStage;
  attemptStatus: AttemptStatus | null;
  mediaCounts: MediaCounts;
  clock: ServerClock;
  /** Server-anchored ms since the last successful beat. */
  msSinceHeartbeat: number | null;
  retry: () => void;
}

/** Stops a bad token from looping forever between claim and 403. */
const MAX_RECLAIMS = 3;

export function usePhoneSession(pairCode: string): PhoneSession {
  const clockRef = useRef(new ServerClock());
  const clock = clockRef.current;
  const reclaimsRef = useRef(0);

  const [state, setState] = useState<SessionState>("connecting");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [claim, setClaim] = useState<ClaimPairCodeResponse | null>(null);
  const [stage, setStage] = useState<AttemptStage>("idle");
  const [attemptStatus, setAttemptStatus] = useState<AttemptStatus | null>(null);
  const [mediaCounts, setMediaCounts] = useState<MediaCounts>({});
  const [lastBeatMs, setLastBeatMs] = useState<number | null>(null);
  const [attempt, setAttempt] = useState(0);

  /* -- claim (or restore) the device token --------------------------- */

  useEffect(() => {
    if (!pairCode) return;
    let cancelled = false;

    (async () => {
      setState((prev) => (prev === "connected" ? "reconnecting" : prev));

      // A stored token means this is a reload, not a first connection.
      const stored = sessionStorage.getItem(tokenKey(pairCode));
      if (stored) {
        try {
          const restored = JSON.parse(stored) as ClaimPairCodeResponse;
          if (!cancelled) {
            setClaim(restored);
            setState("connected");
            setErrorMessage(null);
          }
          return;
        } catch {
          sessionStorage.removeItem(tokenKey(pairCode));
        }
      }

      try {
        const claimed = await claimOnce(pairCode);
        if (cancelled) return;

        sessionStorage.setItem(tokenKey(pairCode), JSON.stringify(claimed));
        setClaim(claimed);
        setState("connected");
        setErrorMessage(null);
      } catch (err) {
        if (cancelled) return;
        setState("failed");
        setErrorMessage(
          isApiError(err, "not_found")
            ? "That code is not valid. Check the code on your laptop."
            : isApiError(err, "conflict")
              ? "This exam has already been submitted."
              : err instanceof Error
                ? err.message
                : "Could not connect.",
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pairCode, attempt]);

  /* -- heartbeat ------------------------------------------------------ */

  useEffect(() => {
    if (!claim) return;

    let cancelled = false;
    let failures = 0;

    const beat = async () => {
      try {
        const res = await timed(clock, () =>
          heartbeat(claim.attempt_id, "phone", claim.device_token),
        );
        if (cancelled) return;

        setStage(res.stage);
        setAttemptStatus(res.attempt_status);
        setMediaCounts(res.media_counts);
        setLastBeatMs(clock.now());
        setState("connected");
        setErrorMessage(null);
        failures = 0;
      } catch (err) {
        if (cancelled) return;
        failures += 1;

        // 401 = token expired. 403 = the server does not recognise this token
        // as the paired phone, which is what a stale token from a superseded
        // claim looks like. Both recover the same way: drop it and re-claim.
        if (isApiError(err, "unauthorized") || isApiError(err, "forbidden")) {
          if (reclaimsRef.current >= MAX_RECLAIMS) {
            setState("failed");
            setErrorMessage(
              "This phone is no longer paired. Get a new code from your laptop.",
            );
            return;
          }
          reclaimsRef.current += 1;
          sessionStorage.removeItem(tokenKey(pairCode));
          setClaim(null);
          setState("reconnecting");
          setAttempt((n) => n + 1);
          return;
        }

        // Anything else is almost certainly the network. Keep beating — the
        // phone must recover on its own, without the student noticing.
        if (failures >= 2) setState("reconnecting");
      }
    };

    void beat();
    const id = setInterval(
      () => void beat(),
      claim.heartbeat_interval_ms || HEARTBEAT_INTERVAL_MS,
    );

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [claim, clock, pairCode]);

  const retry = useCallback(() => {
    reclaimsRef.current = 0;
    sessionStorage.removeItem(tokenKey(pairCode));
    setClaim(null);
    setErrorMessage(null);
    setState("connecting");
    setAttempt((n) => n + 1);
  }, [pairCode]);

  return {
    state,
    errorMessage,
    attemptId: claim?.attempt_id ?? null,
    deviceToken: claim?.device_token ?? null,
    examTitle: claim?.exam_title ?? null,
    studentName: claim?.student_name ?? null,
    stage,
    attemptStatus,
    mediaCounts,
    clock,
    msSinceHeartbeat: lastBeatMs === null ? null : clock.now() - lastBeatMs,
    retry,
  };
}
